// COLOR LIBRARY — the unified color-memory + user-swatch store (2026-07-21).
//
// Owner's ask: "recent colors/gradients/textures, save their own library of
// swatches in their own swatch galleries… the most powerful and user friendly
// system possible for color." This is the DATA LAYER for that: one plain,
// UI-framework-free module (so a future backend sync can adopt it verbatim)
// that remembers what a maker just used and keeps the swatch galleries they
// build by hand.
//
// FillValue is the house grammar EXACTLY as PaintPicker emits it — we never
// invent a new shape, we only remember the ones it already produces:
//   '#rrggbb' / 'rgba(r,g,b,a)'                       (bare-string solid)
//   { type:'solid', color }
//   { type:'linear-gradient', stops:[{offset,color}…], angle }
//   { type:'radial-gradient', stops:[{offset,color}…] }
//   { type:'texture', id, scale? }        (manifest id — ids-never-URLs law)
//   { type:'texture', src, scale? }       (user import — data-URL src)
//
// PERSISTENCE — localStorage key `pseColorLibrary.v1`:
//   { version:1,
//     recents:  { color:[FillValue…], gradient:[…], texture:[…] },   // cap 24
//     galleries:[ { id, name, open, items:[ { v:FillValue, addedAt } … ] } … ] }
//
// LAWS honored here:
//   · localStorage-quota law — writes NEVER throw. When the store would blow
//     past ~4MB we evict the OLDEST saved texture (import-src items are the
//     only heavy payload) and console.info once; a hard quota error degrades
//     to session-only, logged not thrown.
//   · texture-portability law — manifest textures persist by id, user imports
//     by their (already ≤1024 webp) data-URL src, both as PaintPicker emits.
//   · corrupt-JSON law — a garbled store resets to a freshly-migrated seed
//     rather than crashing a picker mount.
//
// FRAMEWORK-FREE: no React here. The only imports are pure color math, so a
// server can require() this file untouched. The React binding (subscribe →
// re-render) lives in LibraryShelf.jsx, not here.
import { parseColor, formatColor } from '../colorMath.mjs';

const LS_KEY = 'pseColorLibrary.v1';
const SCHEMA_VERSION = 1;
const RECENT_CAP = 24;
const MAX_STORE_BYTES = 4 * 1024 * 1024; // ~4MB, under the typical 5MB ceiling

// one-time migration sources — the v1 user rings PaintPicker still reads this
// hour. We FOLD them into a seeded "My colors" gallery and then leave them be:
// we read these keys once, never write them from this store.
const DEFAULT_GALLERY_NAME = 'My colors';
const LEGACY_SWATCHES = 'pseUserSwatches.v1';   // array of hex strings
const LEGACY_GRADIENTS = 'pseUserGradients.v1'; // array of { id, fill }
const LEGACY_TEXTURES = 'pseUserTextures.v1';   // array of { id, label, src }

// ---------------------------------------------------------------------------
// FillValue reading — exported so hosts share ONE reading of a paint.

/** 'color' | 'gradient' | 'texture' from a FillValue; null for none/garbage. */
export function kindOfFill(v) {
  if (v == null) return null;
  if (typeof v === 'string') return 'color';
  if (v.type === 'solid') return 'color';
  if (v.type === 'linear-gradient' || v.type === 'radial-gradient') return 'gradient';
  if (v.type === 'texture') return 'texture';
  return null;
}

/** Canonical color string for a solid (hex/rgba folded to ONE spelling so
 * '#FFF', '#ffffff' and 'rgba(255,255,255,1)' all hash alike). */
function canonColor(c) {
  const p = parseColor(String(c ?? ''));
  return p ? formatColor(p).toLowerCase() : String(c ?? '').trim().toLowerCase();
}

/** Stable serialization of a FillValue for equality. Texture SCALE is left
 * out on purpose: the same marble at 50% and 200% is ONE saved texture, not
 * two library entries. */
function canonicalOf(v) {
  const k = kindOfFill(v);
  if (k === 'color') return `c|${canonColor(typeof v === 'string' ? v : v.color)}`;
  if (k === 'gradient') {
    const stops = (v.stops ?? v.colorStops ?? [])
      .map((s) => `${Math.round((s.offset ?? 0) * 1000)}:${canonColor(s.color)}`)
      .join(',');
    return `g|${v.type}|${v.angle ?? ''}|${stops}`;
  }
  if (k === 'texture') return v.src ? `t|src|${v.src}` : `t|id|${v.id ?? ''}`;
  return 'none';
}

/** Stable, compact hash of a FillValue (FNV-1a → base36). Used as the value
 * identity everywhere: recents dedupe, gallery dedupe, and the opaque handle
 * removeFromGallery/moveItem take. Texture data-URLs never leak as raw keys. */
export function fillHash(v) {
  const s = canonicalOf(v);
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

// ---------------------------------------------------------------------------
// internals — module cache + local pub/sub + cross-tab storage sync.

let cache = null;
const localSubs = new Set();
let storageBound = false;
let quotaLogged = false;
let evictLogged = false;

const isFill = (v) => kindOfFill(v) != null;
const cloneFill = (v) => (typeof v === 'string' ? v : JSON.parse(JSON.stringify(v)));
const newId = (prefix) => `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
const cleanName = (name, fallback) => String(name ?? '').trim().slice(0, 60) || fallback;

function readLegacyArray(key) {
  try {
    const raw = window.localStorage?.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

/** Build a brand-new store, folding the v1 user rings into "My colors".
 * The default gallery is created even when every legacy ring is empty. */
function seedFromLegacy() {
  const items = [];
  const seen = new Set();
  const add = (v) => {
    if (!isFill(v)) return;
    const h = fillHash(v);
    if (seen.has(h)) return;
    seen.add(h);
    items.push({ v: cloneFill(v), addedAt: Date.now() });
  };
  // solids — bare hex strings, most-recent-first
  readLegacyArray(LEGACY_SWATCHES).forEach((c) => { if (typeof c === 'string') add(c); });
  // gradients — { id, fill } wrappers, take the FillValue
  readLegacyArray(LEGACY_GRADIENTS).forEach((p) => {
    const f = p?.fill;
    if (f && (f.type === 'linear-gradient' || f.type === 'radial-gradient')) add(f);
  });
  // textures — { id, label, src } wrappers → the house texture FillValue
  readLegacyArray(LEGACY_TEXTURES).forEach((t) => {
    if (t && typeof t.src === 'string') add({ type: 'texture', src: t.src, ...(t.label ? { label: t.label } : {}), scale: 1 });
    else if (t && typeof t.id === 'string') add({ type: 'texture', id: t.id, scale: 1 });
  });
  return {
    version: SCHEMA_VERSION,
    recents: { color: [], gradient: [], texture: [] },
    galleries: [{ id: newId('g'), name: DEFAULT_GALLERY_NAME, open: true, items }],
  };
}

/** Coerce a parsed blob into a well-formed store, dropping anything malformed.
 * Returns null when the blob is too broken to trust (→ reseed). */
function normalize(obj) {
  if (!obj || typeof obj !== 'object' || obj.version !== SCHEMA_VERSION) return null;
  const r = obj.recents;
  if (!r || typeof r !== 'object' || !Array.isArray(obj.galleries)) return null;
  const recents = {
    color: (Array.isArray(r.color) ? r.color : []).filter(isFill).map(cloneFill).slice(0, RECENT_CAP),
    gradient: (Array.isArray(r.gradient) ? r.gradient : []).filter(isFill).map(cloneFill).slice(0, RECENT_CAP),
    texture: (Array.isArray(r.texture) ? r.texture : []).filter(isFill).map(cloneFill).slice(0, RECENT_CAP),
  };
  const galleries = obj.galleries
    .filter((g) => g && typeof g === 'object')
    .map((g) => ({
      id: typeof g.id === 'string' ? g.id : newId('g'),
      name: cleanName(g.name, DEFAULT_GALLERY_NAME),
      open: g.open !== false,
      items: (Array.isArray(g.items) ? g.items : [])
        .filter((it) => it && isFill(it.v))
        .map((it) => ({ v: cloneFill(it.v), addedAt: Number.isFinite(it.addedAt) ? it.addedAt : Date.now() })),
    }));
  return { version: SCHEMA_VERSION, recents, galleries };
}

function loadStore() {
  if (cache) return cache;
  let parsed = null;
  try {
    const raw = window.localStorage?.getItem(LS_KEY);
    if (raw) parsed = JSON.parse(raw);
  } catch { parsed = null; } // corrupt / private mode → reseed below
  const normalized = parsed ? normalize(parsed) : null;
  if (normalized) {
    cache = normalized;
  } else {
    cache = seedFromLegacy();
    persist(); // stamp the migrated seed so we migrate exactly once
  }
  return cache;
}

/** Walk every saved texture-with-src item, oldest addedAt first. */
function textureItemsOldestFirst(store) {
  const out = [];
  store.galleries.forEach((g) => {
    g.items.forEach((it) => {
      if (kindOfFill(it.v) === 'texture' && it.v.src) out.push({ gallery: g, item: it });
    });
  });
  return out.sort((a, b) => (a.item.addedAt ?? 0) - (b.item.addedAt ?? 0));
}

/** Trim oldest saved textures until the serialized store fits under the cap.
 * Returns how many were evicted (0 when nothing needed doing). */
function enforceCap(store) {
  let removed = 0;
  let heavy = textureItemsOldestFirst(store);
  while (heavy.length && JSON.stringify(store).length > MAX_STORE_BYTES) {
    const { gallery, item } = heavy.shift();
    gallery.items = gallery.items.filter((it) => it !== item);
    removed += 1;
    heavy = textureItemsOldestFirst(store); // list shifts as galleries shrink
  }
  if (removed && !evictLogged) {
    // quiet, one-time — the maker's work is preserved except the oldest tiles
    console.info(`[colorLibrary] Storage near limit — removed ${removed} oldest saved texture(s) to make room.`);
    evictLogged = true;
  }
  return removed;
}

function persist() {
  const store = cache;
  if (!store) return;
  enforceCap(store);
  try {
    window.localStorage?.setItem(LS_KEY, JSON.stringify(store));
  } catch {
    // hard quota (or private mode): keep it in memory for the session, logged
    if (!quotaLogged) {
      console.info('[colorLibrary] Storage unavailable — your color library is kept for this session only.');
      quotaLogged = true;
    }
  }
  localSubs.forEach((fn) => { try { fn(); } catch { /* a bad subscriber never breaks a write */ } });
}

function bindStorage() {
  if (storageBound || typeof window === 'undefined' || !window.addEventListener) return;
  window.addEventListener('storage', (e) => {
    if (e.key !== LS_KEY) return;
    cache = null; // another tab rewrote it — drop our copy and re-read
    loadStore();
    localSubs.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
  });
  storageBound = true;
}

// ---------------------------------------------------------------------------
// public API — all synchronous, all defensive.

/** Remember a paint the maker just committed, on the ring for its kind
 * (dedupe by value, most-recent-first, cap 24). `kind` is trusted when valid,
 * else derived from the value. No-op for live drag ticks — hosts call this at
 * the Apply/commit seam, never per tick. */
export function noteRecent(kind, v) {
  if (!isFill(v)) return;
  const k = validKind(kind) ?? kindOfFill(v);
  if (!k) return;
  const store = loadStore();
  const h = fillHash(v);
  store.recents[k] = [cloneFill(v), ...store.recents[k].filter((x) => fillHash(x) !== h)].slice(0, RECENT_CAP);
  persist();
}

/** The recents ring for a kind ('color' | 'gradient' | 'texture'). */
export function getRecents(kind) {
  const k = validKind(kind) ?? 'color';
  return loadStore().recents[k].slice();
}

/** Every gallery, in order (defensive copies — callers can't mutate the store). */
export function listGalleries() {
  return loadStore().galleries.map((g) => ({
    id: g.id, name: g.name, open: g.open, items: g.items.slice(),
  }));
}

/** Make a new (empty) gallery; returns it. */
export function createGallery(name) {
  const store = loadStore();
  const g = { id: newId('g'), name: cleanName(name, 'New gallery'), open: true, items: [] };
  store.galleries = [...store.galleries, g];
  persist();
  return { ...g, items: [] };
}

export function renameGallery(id, name) {
  const g = loadStore().galleries.find((x) => x.id === id);
  if (!g) return;
  g.name = cleanName(name, g.name || DEFAULT_GALLERY_NAME);
  persist();
}

export function deleteGallery(id) {
  const store = loadStore();
  store.galleries = store.galleries.filter((g) => g.id !== id);
  persist();
}

/** Save the current paint into a gallery (dedupe within it, move-to-front). */
export function saveToGallery(galleryId, v) {
  if (!isFill(v)) return;
  const g = loadStore().galleries.find((x) => x.id === galleryId);
  if (!g) return;
  const h = fillHash(v);
  g.items = [{ v: cloneFill(v), addedAt: Date.now() }, ...g.items.filter((it) => fillHash(it.v) !== h)];
  persist();
}

/** Remove one saved item from a gallery, addressed by its fillHash. */
export function removeFromGallery(galleryId, hash) {
  const g = loadStore().galleries.find((x) => x.id === galleryId);
  if (!g) return;
  g.items = g.items.filter((it) => fillHash(it.v) !== hash);
  persist();
}

/** Move a saved item between galleries, addressed by its fillHash (dedupe in
 * the destination, freshens addedAt). A no-op when either end is missing. */
export function moveItem(fromId, toId, hash) {
  if (fromId === toId) return;
  const store = loadStore();
  const from = store.galleries.find((x) => x.id === fromId);
  const to = store.galleries.find((x) => x.id === toId);
  if (!from || !to) return;
  const moved = from.items.find((it) => fillHash(it.v) === hash);
  if (!moved) return;
  from.items = from.items.filter((it) => it !== moved);
  to.items = [{ v: cloneFill(moved.v), addedAt: Date.now() }, ...to.items.filter((it) => fillHash(it.v) !== hash)];
  persist();
}

/** Persist a gallery's expanded/collapsed state. */
export function setGalleryOpen(id, open) {
  const g = loadStore().galleries.find((x) => x.id === id);
  if (!g) return;
  g.open = !!open;
  persist();
}

/** Subscribe to any change (local writes + other-tab storage events). Returns
 * an unsubscribe. Every mounted shelf subscribes so they all stay live. */
export function subscribe(fn) {
  bindStorage();
  localSubs.add(fn);
  return () => localSubs.delete(fn);
}

function validKind(k) {
  return (k === 'color' || k === 'gradient' || k === 'texture') ? k : null;
}
